import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Eye, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";

export default function RelatedQuestions({ currentSlug, category, limit = 5 }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRelated = async () => {
      if (!category) {
        setLoading(false);
        return;
      }
      
      try {
        // Fetch questions from same category, excluding current
        const res = await api.get(`/feed?category=${encodeURIComponent(category)}&limit=${limit + 1}`);
        const filtered = (res.data || []).filter(q => q.slug !== currentSlug).slice(0, limit);
        setQuestions(filtered);
      } catch (e) {
        console.error("Failed to fetch related questions:", e);
      } finally {
        setLoading(false);
      }
    };
    
    fetchRelated();
  }, [category, currentSlug, limit]);

  if (loading) {
    return (
      <div className="mt-8 pt-6 border-t border-slate-800">
        <h2 className="text-lg font-semibold text-slate-200 mb-4">Related Questions</h2>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-slate-800/50 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (questions.length === 0) return null;

  return (
    <div className="mt-8 pt-6 border-t border-slate-800">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-200">
          More in {category}
        </h2>
        <Link 
          to={`/browse?category=${encodeURIComponent(category)}`}
          className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1"
        >
          View all
          <ChevronRight className="h-4 w-4" />
        </Link>
      </div>
      
      <div className="space-y-3">
        {questions.map((q) => (
          <Link key={q.slug} to={`/q/${q.slug}`}>
            <Card className="p-4 bg-slate-800/30 border-slate-700/50 hover:border-indigo-500/50 hover:bg-slate-800/50 transition-all cursor-pointer group">
              <h3 className="text-slate-200 font-medium line-clamp-2 group-hover:text-indigo-300 transition-colors">
                {q.title || q.body?.slice(0, 100)}
              </h3>
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <MessageCircle className="h-3.5 w-3.5" />
                  {q.answer_count || 0} answers
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="h-3.5 w-3.5" />
                  {q.view_count || 0} views
                </span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
